import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  MoviProducaoContextData,
  MoviProducaoProviderProps,
  FormType,
} from '../types/context';
import { GridRowParams } from '@material-ui/data-grid';
import { toast } from 'react-toastify';
import { insertOrUpdate, MasterDetailProps } from '~/utils/masterDetail';
import { schema } from '../validations';
import { LojaContext } from '~/context/loja';
import {
  dataProps,
  formDataProps,
  rowProps,
  SelectProps,
  tiposProps,
  totalizadoresProps,
} from '../types';
import { servicesMovProd } from '../services';
import {
  calcTotalizadores,
  calcular,
  calcTotalizadoresOnRowClick,
} from '../functions';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import { formDefault } from '../defaultData';
import { nanoid } from 'nanoid';

const masterDetailDefault = [
  {
    obj_name: 'produtos',
    pk_fields: ['cod_produto_producao'],
    itens: {
      insert: [],
      update: [],
      delete: [],
    },
  },
];

export const MoviProducaoContext = createContext({} as MoviProducaoContextData);

export const MoviProducaoProvider: React.FC<MoviProducaoProviderProps> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [disabledProd, setDisabledProd] = useState<boolean>(false);
  const [flgCancelado, setFlgCancelado] = useState<boolean>(false);
  const [rows, setRows] = useState<rowProps[]>([]);
  const [ajustePais, setAjustePais] = useState<SelectProps[]>([]);
  const [ajusteFilhos, setAjusteFilhos] = useState<SelectProps[]>([]);
  const [totalizadores, setTotalizadores] = useState<totalizadoresProps>({
    tot_custo_producao: 0,
    tot_qtd_producao: 0,
  });
  const { loja } = useContext(LojaContext);

  const [masterDetail, setMasterDetail] =
    useState<MasterDetailProps[]>(masterDetailDefault);

  const { getTiposdeEstoque, getProducao, createProduction, cancelProduction } =
    servicesMovProd;

  // form Geral
  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });
  const formTela: FormType = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  };

  const getTipos = useCallback(async () => {
    const res = await getTiposdeEstoque();
    const pais = res.rowsPais.map((i: tiposProps) => {
      return {
        value: i.cod_tipo_ajuste,
        label: `${i.cod_tipo_ajuste} - ${i.des_operacao} ${i.des_ajuste}`,
      };
    });
    const filhos = res.rowsFilhos.map((i: tiposProps) => {
      return {
        value: i.cod_tipo_ajuste,
        label: `${i.cod_tipo_ajuste} - ${i.des_operacao} ${i.des_ajuste}`,
      };
    });

    setAjustePais(pais);
    setAjusteFilhos(filhos);
  }, [getTiposdeEstoque]);

  useEffect(() => {
    getTipos();
  }, [getTipos]);

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
  };

  const handleCalcular = useCallback(async () => {
    const qtdProd = transformAsCurrency(watch('qtd_producao'));
    if (!qtdProd) return setError('qtd_producao', { type: 'required' });
    try {
      setLoadingButton(true);
      const resultCalc = calcular(qtdProd, rows);
      const insertRows: any = resultCalc.map((i: any) => {
        return {
          ...i,
          tot_qtd_producao: i.qtd_total,
          tot_custo_producao: i.val_custo_producao_total,
        };
      });

      const resTotalizadores = calcTotalizadores(insertRows);
      setTotalizadores({
        tot_custo_producao: resTotalizadores.tot_custo_producao,
        tot_qtd_producao: resTotalizadores.tot_qtd_producao,
      });
      const rowsDetail: any = await insertOrUpdate(
        'produtos',
        insertRows,
        masterDetail,
        setMasterDetail,
      );
      setRows(rowsDetail.flat());
      setLoadingButton(false);
    } catch (error: any) {
      toast.warn(error.message);
    }
  }, [rows, watch, masterDetail, setError]);

  const resetFormData = useCallback(() => {
    reset({
      ...formDefault,
      cod_loja: {
        value: loja.cod_loja,
        label: `${loja.des_loja} (${loja.des_cidade})`,
      },
    });
    setMasterDetail(JSON.parse(JSON.stringify(masterDetailDefault)));
    setRows([]);
    setDisabledProd(false);
    setFlgCancelado(false);
    setTotalizadores({
      tot_custo_producao: 0,
      tot_qtd_producao: 0,
    });
  }, [reset, setMasterDetail, loja]);

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      try {
        const { row } = param;
        const {
          cod_loja,
          des_loja,
          des_loja_cidade,
          cod_produto,
          cod_seq_producao,
          cod_tipo_ajuste_pai,
          cod_tipo_ajuste_filho,
          flg_cancelada,
          des_produto,
          qtd_producao,
        } = row;
        const tipoAjustePai = ajustePais.find(
          (i) => i.value === cod_tipo_ajuste_pai,
        );
        const tipoAjusteFilho = ajusteFilhos.find(
          (i) => i.value === cod_tipo_ajuste_filho,
        );
        setFlgCancelado(flg_cancelada);

        reset({
          cod_loja: {
            value: cod_loja,
            label: `${des_loja} (${des_loja_cidade})`,
          },
          busca_produto: {
            value: cod_produto,
            label: `${cod_produto} - ${des_produto}`,
          },
          cod_seq_producao,
          cod_tipo_ajuste_pai: tipoAjustePai,
          cod_tipo_ajuste_filho: tipoAjusteFilho,
          des_produto,
          qtd_producao: formatCurrencyAsText(qtd_producao, 3, 3),
        });
        const { data, success } = await getProducao(cod_seq_producao);
        if (success) {
          const res: any = data.map((i) => {
            const uuid = nanoid();
            return {
              ...i,
              uuid,
              val_custo_producao: i.qtd_producao * i.val_custo_rep,
              tot_qtd_producao: i.tot_qtd_producao,
              tot_custo_producao: i.tot_custo_producao,
            };
          });
          const rowsDetail: any = await insertOrUpdate(
            'produtos',
            res,
            masterDetail,
            setMasterDetail,
          );
          setRows(rowsDetail.flat());
          const resTotalizadores = calcTotalizadoresOnRowClick(res);
          setTotalizadores({
            tot_custo_producao: resTotalizadores.tot_custo_producao,
            tot_qtd_producao: resTotalizadores.tot_qtd_producao,
          });
        }

        setUpdate(true);
        setLoading(false);
        setShowSearch(false);
      } catch (error: any) {
        toast.warn(error.message);
      }
    },
    [ajustePais, ajusteFilhos, getProducao, reset, masterDetail],
  );

  const prepFormData = (data: any): formDataProps => {
    const formData: dataProps = {
      cod_loja: data.cod_loja.value,
      cod_produto: data.busca_produto.value,
      cod_tipo_ajuste_pai: data.cod_tipo_ajuste_pai.value,
      cod_tipo_ajuste_filho: data.cod_tipo_ajuste_filho.value,
      qtd_producao: transformAsCurrency(data.qtd_producao),
    };
    return {
      formData,
      masterDetail,
    };
  };

  const onSave = handleSubmit(async (data) => {
    const isComplete = rows.some((i) => {
      if (!i.tot_custo_producao || !i.tot_qtd_producao) return false;

      return true;
    });
    if (!isComplete) {
      return toast.warn(
        'É necessário realizar o cálculo antes de gravar o registro',
      );
    }
    try {
      const payload: formDataProps = prepFormData(data);
      const { success, message } = await createProduction(payload);
      if (success) {
        setShowSearch(false);
        resetFormData();
        setDisabledProd(false);
        return toast.success(message);
      }
    } catch (e: any) {
      toast.warn(e.message);
    }
  });

  const handleCancelProd = useCallback(async () => {
    if (flgCancelado) return toast.warn('Produção já cancelada');
    try {
      const { success, message } = await cancelProduction(
        watch('cod_seq_producao'),
      );
      if (success) {
        toast.success(message);
        resetFormData();
        setShowSearch(true);
        setUpdate(false);
      }
    } catch (error: any) {
      toast.warn(error.message);
    }
  }, [resetFormData, cancelProduction, watch, flgCancelado]);

  return (
    <MoviProducaoContext.Provider
      value={{
        isUpdate,
        setUpdate,
        showSearch,
        setShowSearch,
        loading,
        setLoading,
        loadingTable,
        setLoadingTable,
        loadingButton,
        setLoadingButton,
        disabledProd,
        setDisabledProd,
        flgCancelado,
        setFlgCancelado,
        rows,
        setRows,
        ajustePais,
        ajusteFilhos,
        totalizadores,
        setTotalizadores,
        loja,
        formTela,
        resetFormData,
        onRowClick,
        onNew,
        onSave,
        handleCancelProd,
        handleCalcular,

        masterDetail,
        setMasterDetail,
      }}
    >
      {children}
    </MoviProducaoContext.Provider>
  );
};
